import React, { useEffect, useRef, useState } from 'react';
import { uploadImages } from '../lib/image-upload';

/** Apple platforms use Cmd, everything else (Linux, Windows, ChromeOS) Strg. */
function istApple(): boolean {
  if (typeof navigator === 'undefined') return false;
  const uaPlatform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform;
  return /mac|iphone|ipad|ipod/i.test(uaPlatform || navigator.platform || navigator.userAgent || '');
}

/**
 * Why a direct clipboard read cannot work here, if that is known up front: an embedding without
 * `allow="clipboard-read"` (Permissions-Policy) or an Electron view. Then "permission denied" would
 * blame the user for something they cannot change in the browser.
 */
function leseSperre(): string | null {
  if (typeof document === 'undefined') return null;
  const policy = (document as Document & { permissionsPolicy?: { allowsFeature?: (f: string) => boolean }; featurePolicy?: { allowsFeature?: (f: string) => boolean } });
  const erlaubt = (policy.permissionsPolicy ?? policy.featurePolicy)?.allowsFeature?.('clipboard-read');
  if (erlaubt === false) return 'Diese Ansicht lässt das direkte Lesen der Zwischenablage nicht zu (Permissions-Policy der Seite bzw. des eingebetteten Fensters).';
  return null;
}
function eingebettet(): boolean {
  try { return typeof window !== 'undefined' && window.self !== window.top; } catch { return true; } // silent-ok: cross-origin top access throws, which itself means embedded
}

/** Shared image paste control: original bytes, visible progress and retained retry. */
export function BildEinfuegeZone({ onText, onFehler, onBusy, hoehe = 48, aiId = 'bild-einfuege-zone' }: {
  onText: (readCommand: string) => void;
  onFehler?: (nachricht: string) => void;
  onBusy?: (busy: boolean) => void;
  hoehe?: number;
  aiId?: string;
}) {
  const zone = useRef<HTMLDivElement>(null);
  const running = useRef<AbortController | null>(null);
  const pending = useRef<File[]>([]);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState('');
  /** `hinweis` = operating hint at the field only; `fehler` = real failure, also reported via onFehler. */
  const [art, setArt] = useState<'hinweis' | 'fehler'>('fehler');
  const pasteKey = istApple() ? 'Cmd+V' : 'Strg+V';
  useEffect(() => () => { running.current?.abort(); onBusy?.(false); }, []);

  async function start(files: File[]) {
    if (running.current) return; // repeated paste must not duplicate a pending upload
    const controller = new AbortController();
    running.current = controller;
    pending.current = files;
    setError(''); setProgress(0); onBusy?.(true);
    try {
      const data = await uploadImages(files, { signal: controller.signal, onProgress: setProgress });
      if (!controller.signal.aborted) { pending.current = []; onText(data.readCommand); }
    } catch (err) {
      if (!controller.signal.aborted) {
        const message = err instanceof Error ? err.message : String(err);
        setArt('fehler'); setError(message); onFehler?.(message);
      }
    } finally {
      if (!controller.signal.aborted) { running.current = null; setProgress(null); onBusy?.(false); }
    }
  }
  /** Operating hint: shown at the field only, never in the caller's error band; a pending retry stays. */
  function hinweis(message: string) { setArt('hinweis'); setError(message); }
  /** Real failure (clipboard could not be read): at the field and via onFehler. */
  function fehler(message: string) { setArt('fehler'); setError(message); onFehler?.(message); }
  function paste(e: React.ClipboardEvent) {
    const files = Array.from(e.clipboardData.items).filter(i => i.type.startsWith('image/')).map(i => i.getAsFile()).filter((f): f is File => !!f);
    e.preventDefault(); e.stopPropagation();
    if (!files.length) { hinweis(`In der Zwischenablage liegt kein Bild. Bitte zuerst einen Screenshot oder ein Bild kopieren, dann erneut ${pasteKey} drücken.`); return; }
    void start(files);
  }
  /** Click: read the clipboard directly where the browser allows it; otherwise keep focus for the keyboard paste and say why. */
  async function clickRead() {
    zone.current?.focus();
    if (running.current) return;
    const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (!clip || typeof clip.read !== 'function') {
      hinweis(typeof window !== 'undefined' && window.isSecureContext === false
        ? `Über diese Adresse (ohne HTTPS) gibt der Browser die Zwischenablage nicht per Klick frei. Das Feld ist jetzt aktiv: bitte ${pasteKey} drücken.`
        : `Dieser Browser bietet das direkte Lesen der Zwischenablage nicht an. Das Feld ist jetzt aktiv: bitte ${pasteKey} drücken.`);
      return;
    }
    const sperre = leseSperre();
    if (sperre) { hinweis(`${sperre} Das Feld ist jetzt aktiv: bitte ${pasteKey} drücken.`); return; }
    let items: ClipboardItems;
    try {
      items = await clip.read();
    } catch (err) {
      const name = err instanceof DOMException ? err.name : '';
      const electron = typeof navigator !== 'undefined' && /Electron/i.test(navigator.userAgent);
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        hinweis(eingebettet() || electron
          ? `Diese ${electron ? 'App-Ansicht' : 'eingebettete Ansicht'} gibt die Zwischenablage nicht per Klick frei (vom einbettenden Fenster gesperrt, nicht im Browser einstellbar). Bitte im aktiven Feld ${pasteKey} drücken.`
          : `Der Browser hat das Lesen der Zwischenablage abgelehnt (Einfügen-Abfrage abgebrochen oder Berechtigung für diese Seite gesperrt). Bitte erlauben oder im aktiven Feld ${pasteKey} drücken.`);
      } else if (name === 'DataError' || name === 'NotFoundError') {
        hinweis('In der Zwischenablage liegt kein Bild. Bitte zuerst einen Screenshot oder ein Bild kopieren.');
      } else {
        fehler(`Die Zwischenablage konnte nicht gelesen werden (${err instanceof Error ? err.message : String(err)}). Bitte im aktiven Feld ${pasteKey} drücken.`);
      }
      return;
    }
    const files: File[] = [];
    try {
      for (const [n, item] of items.entries()) {
        const type = item.types.find(t => t.startsWith('image/'));
        if (!type) continue;
        const blob = await item.getType(type);
        files.push(new File([blob], `zwischenablage-${Date.now()}-${n + 1}.${type.slice(6).split('+')[0] || 'png'}`, { type }));
      }
    } catch (err) {
      fehler(`Das Bild aus der Zwischenablage konnte nicht gelesen werden (${err instanceof Error ? err.message : String(err)}). Bitte im aktiven Feld ${pasteKey} drücken.`);
      return;
    }
    if (!files.length) { hinweis('In der Zwischenablage liegt kein Bild. Bitte zuerst einen Screenshot oder ein Bild kopieren.'); return; }
    void start(files);
  }
  const label = progress === null ? `Bild einfügen: hier klicken (liest die Zwischenablage) oder ${pasteKey}` : progress === 100 ? 'Bild wird gespeichert…' : `Bild hochladen: ${progress} %`;
  return <div style={{ position: 'relative', flexShrink: 0 }}>
    <div data-ai-id={aiId} ref={zone} role="button" aria-label={label} aria-busy={progress !== null}
      tabIndex={0} onPaste={paste} onClick={() => void clickRead()}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void clickRead(); } }}
      title={label} style={{ width: 44, minHeight: hoehe, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: progress !== null ? 'rgba(168,85,247,0.15)' : 'var(--tn-bg)', border: `1px solid ${progress !== null ? 'var(--tnf-a855f7)' : 'var(--tn-border)'}`,
        borderRadius: 4, cursor: 'pointer', fontSize: progress !== null ? 11 : 16, color: 'var(--tn-text)', flexShrink: 0 }}>
      <span aria-live="polite">{progress === null ? '📋' : progress === 100 ? '⏳' : `${progress}%`}</span>
    </div>
    {error && <div role={art === 'fehler' ? 'alert' : 'status'} style={{ position: 'absolute', bottom: '100%', left: 0, zIndex: 20, width: 260, padding: 10, borderRadius: 5, background: 'var(--tn-bg, var(--tnfh-20202b))', border: `1px solid ${art === 'fehler' ? 'var(--tnf-ef4444)' : 'var(--tn-border)'}`, color: 'var(--tn-text, white)', fontSize: 12 }}>
      {error}<br />{pending.current.length > 0 && <button type="button" disabled={progress !== null} onClick={() => void start(pending.current)}>Erneut versuchen</button>}
      <button type="button" onClick={() => { pending.current = []; setError(''); }}>Schließen</button>
    </div>}
  </div>;
}
